import { api } from '../api-client'
import { isGitRepo } from '../git'
import { resolveRepo, notLinkedMessage } from '../repo-resolver'
import { analyticsCommandError, analyticsEvent, flushAndExit } from '../analytics'

function parseArgs(args: string[]): { flags: Record<string, string | boolean> } {
  const flags: Record<string, string | boolean> = {}
  for (let i = 0; i < args.length; i++) {
    if (args[i].startsWith('--')) {
      const key = args[i].slice(2)
      const next = args[i + 1]
      if (next && !next.startsWith('--')) { flags[key] = next; i++ }
      else flags[key] = true
    }
  }
  return { flags }
}

function emitJSON(data: unknown): void {
  console.log(JSON.stringify(data, null, 2))
}

function isTransientResolutionFailure(resolution: Awaited<ReturnType<typeof resolveRepo>>): boolean {
  if (resolution.ok || resolution.reason !== 'no_workspace') return false
  const d = resolution.diagnostics
  const text = [d?.meError, d?.failedEndpoint, resolution.detail].filter(Boolean).join(' ')
  return /Connection failed|timed out|ENOTFOUND|ECONNRESET|ECONNREFUSED|socket hang up/i.test(text)
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function syncCommand(args: string[]): Promise<void> {
  if (!isGitRepo()) {
    console.error('Error: not a git repository.')
    process.exit(1)
  }

  const { flags } = parseArgs(args)
  const isJSON = !!flags['json']

  let resolution = await resolveRepo()
  if (isTransientResolutionFailure(resolution)) {
    await delay(500)
    resolution = await resolveRepo()
  }
  if (!resolution.ok) {
    if (resolution.reason === 'no_remote') {
      if (!isJSON) console.log('⚠ Error: no git remote configured.')
      else emitJSON({ error: 'no_remote', message: 'no git remote configured' })
      process.exit(1)
    }

    if (resolution.reason === 'not_linked') {
      const msg = notLinkedMessage(resolution.remote!, resolution.workspaceSlug!)
      if (!isJSON) {
        console.log(`⚠ ${msg}`)
        console.log('  Run `driftless doctor` to diagnose.')
      } else {
        emitJSON({ error: 'not_linked', message: msg, diagnostics: resolution.diagnostics })
      }
      await analyticsCommandError('sync', resolution.workspaceSlug, msg)
      await flushAndExit(1)
    }

    // no_workspace — surface the full diagnostic instead of a silent slug list
    if (!isJSON) {
      console.log('⚠ Error: could not resolve workspace.')
      if (resolution.detail) console.log(resolution.detail)
      console.log('  Run `driftless doctor` to diagnose.')
    } else {
      emitJSON({
        error: 'no_workspace',
        message: 'could not resolve workspace',
        diagnostics: resolution.diagnostics,
      })
    }
    process.exit(1)
  }

  const { workspaceSlug, repoId, remote, source } = resolution

  // Driftless = drift-awareness around topics, not a firehose. sync answers
  // ONE question: "what moved around my topics since I last looked?" The
  // deduped signal is `stale` (a topic whose covered code the team changed —
  // see webhooks handlePush). PR activity = what the team shipped against
  // this repo's context. The raw per-event feed is not surfaced here on
  // purpose (noise / off-narrative).
  const [staleTopics, suggestedTopics, prActivity, reposRes] = await Promise.allSettled([
    api.get(`/workspaces/${workspaceSlug}/topics?stale=true&repo=${repoId}`) as Promise<any[]>,
    api.get(`/workspaces/${workspaceSlug}/topics?suggested=true&repo=${repoId}`) as Promise<any[]>,
    api.get(`/workspaces/${workspaceSlug}/pr-activity?repo_id=${repoId}&limit=5`) as Promise<{ items?: any[] } | any[]>,
    api.get(`/workspaces/${workspaceSlug}/repos`) as Promise<any[]>,
  ])

  const stale = staleTopics.status === 'fulfilled' ? staleTopics.value : []
  const suggested = suggestedTopics.status === 'fulfilled' ? suggestedTopics.value : []
  const rawPr = prActivity.status === 'fulfilled' ? prActivity.value : []
  const prs: any[] = Array.isArray(rawPr) ? rawPr : (rawPr as any).items ?? []
  // Make the drift scope visible — sync's `stale` only reflects pushes to
  // these branches, so a dev whose feature-branch push didn't show up can
  // see why (it's not a black box).
  const repoRow =
    reposRes.status === 'fulfilled' && Array.isArray(reposRes.value)
      ? reposRes.value.find((r: any) => r.id === repoId)
      : undefined
  const trackedDefault: string | null = repoRow?.default_branch ?? null
  const trackedExtra: string[] = repoRow?.tracked_branches ?? []
  const trackingBranchCount = (trackedDefault ? 1 : 0) + trackedExtra.length
  analyticsEvent('cli_sync', workspaceSlug, {
    stale_count: stale.length,
    pr_activity_count: prs.length,
    suggested_count: suggested.length,
    tracking_branch_count: trackingBranchCount,
  })

  if (isJSON) {
    emitJSON({
      repo: `${remote.org}/${remote.repo}`,
      workspace: workspaceSlug,
      resolved_via: source,
      tracking: { default_branch: trackedDefault, extra_branches: trackedExtra },
      stale_topics: stale.map((t: any) => ({ topic: t.topic, reason: t.stale?.reason ?? null })),
      pr_activity: prs.slice(0, 5).map((p: any) => ({
        pr_number: p.pr_number,
        title: p.pr_title,
        author: p.pr_author,
        risk: p.risk ?? null,
        topics: p.topic_slugs ?? [],
        observed_at: p.observed_at,
      })),
      suggested_pending: suggested.length,
    })
    await flushAndExit(0)
  }

  console.log(`▌ ${remote.org}/${remote.repo}`)
  console.log(`  workspace: ${workspaceSlug}  (resolved via ${source})`)
  {
    // When the API has not seen a push yet (e.g. GitHub App not installed),
    // default_branch is null. Don't print a bare "(default branch)" — that
    // reads as a real branch named that. Say so honestly.
    const defaultLabel = trackedDefault ?? '(default branch — unresolved; install GitHub App to populate)'
    const scope = [defaultLabel, ...trackedExtra].filter(Boolean).join(', ')
    console.log(`  tracking:  ${scope}  (drift only from these — \`driftless branches\` to change)\n`)
  }

  // The headline: topics whose covered code the team changed since you last
  // looked. This is the whole point of sync — what drifted around you.
  if (stale.length > 0) {
    console.log(`⚠  ${stale.length} topic${stale.length === 1 ? '' : 's'} drifted — the team changed code these topics cover, context may be behind:`)
    for (const t of stale) {
      console.log(`   ${t.topic}`)
      if (t.stale?.reason) console.log(`     ${t.stale.reason}`)
      console.log(`     → review the change; if it changed how this works: driftless context update ${t.topic} --what "..." --how "..."`)
    }
    console.log('')
  }

  // What the team shipped against this repo's context (observed by the
  // GitHub App on the remote, not from your local tree).
  if (prs.length > 0) {
    console.log(`Team activity (${prs.length} PR${prs.length === 1 ? '' : 's'} via GitHub App):`)
    for (const p of prs.slice(0, 5)) {
      const risk = p.risk ? `[${p.risk}] ` : ''
      const topics = (p.topic_slugs ?? []).length ? `  → touched ${(p.topic_slugs ?? []).join(', ')}` : ''
      console.log(`  ${risk}#${p.pr_number} ${String(p.pr_title ?? '').slice(0, 70)}${topics}`)
    }
    console.log('')
  }

  // Suggested topics pending agent review
  if (suggested.length > 0) {
    console.log(`${suggested.length} suggested topic${suggested.length === 1 ? '' : 's'} from init — review and confirm:`)
    console.log(`  driftless context list --suggested`)
    console.log('')
  }

  if (stale.length === 0 && suggested.length === 0 && prs.length === 0) {
    console.log('Nothing drifted around your topics. You are up to date.')
  } else if (stale.length > 0) {
    console.log('Review the drifted topics before touching code — their context may be stale.')
  }

  await flushAndExit(0)
}
